// Live Monitoring Service for NLFX Cable Builder
// Real-time tracking and monitoring of user activity

import requestQueue from './requestQueue.js';

class LiveMonitoringService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.userId = this.getUserId();
    this.startTime = Date.now();
    this.currentStep = 0;
    this.isActive = true;
    this.lastActivity = Date.now();
    this.heartbeatInterval = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.activityThrottleTimer = null;
    this.lastActivityUpdate = 0;
    this.activityUpdateCooldown = 10000; // 10 seconds between activity updates
    
    // Detect if we're in embedded mode
    this.isEmbedded = this.detectEmbeddedMode();
    this.apiBaseUrl = this.getApiBaseUrl();
    
    this.init();
  }

  detectEmbeddedMode() {
    // Check URL parameters for embed flag
    const urlParams = new URLSearchParams(window.location.search);
    const hasEmbedParam = urlParams.get('embed') === 'true';
    
    // Check if we're in an iframe
    const isInIframe = window.self !== window.top;
    
    // Check referrer to see if we came from another domain
    const referrer = document.referrer;
    const currentDomain = window.location.hostname;
    const referrerDomain = referrer ? new URL(referrer).hostname : null;
    const isFromDifferentDomain = referrerDomain && referrerDomain !== currentDomain;
    
    return hasEmbedParam || isInIframe || isFromDifferentDomain;
  }

  getApiBaseUrl() {
    // If embedded, always use the main app domain for API calls
    if (this.isEmbedded) {
      // Use the main app domain for API calls
      return 'https://nlfx-cable.luknai.com';
    }
    
    // Otherwise use the current domain
    return window.location.origin;
  }

  init() {
    this.startHeartbeat();
    this.setupActivityTracking();
    this.trackSessionStart();
    
    // Log embedded mode detection
    if (this.isEmbedded) {
      console.log('🔗 Live monitoring: Embedded mode detected, using API base URL:', this.apiBaseUrl);
    }
  }

  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  getUserId() {
    let userId = localStorage.getItem('analytics_user_id');
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('analytics_user_id', userId);
    }
    return userId;
  }

  startHeartbeat() {
    // Send heartbeat every 2 minutes to keep session active
    this.heartbeatInterval = setInterval(() => {
      this.updateActivity();
    }, 120000); // 2 minutes to reduce serverless function load
  }

  setupActivityTracking() {
    // Track user activity - only on significant events, not every mouse move
    const activityEvents = ['click', 'keypress', 'scroll'];
    
    activityEvents.forEach(eventType => {
      document.addEventListener(eventType, () => {
        this.updateActivity();
      }, { passive: true });
    });

    // Track page visibility
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.setInactive();
      } else {
        this.setActive();
      }
    });

    // Track before unload
    window.addEventListener('beforeunload', () => {
      this.trackSessionEnd();
    });
  }

  updateActivity() {
    this.lastActivity = Date.now();
    this.isActive = true;
    
    // Throttle activity updates to prevent overwhelming the server
    const now = Date.now();
    if (now - this.lastActivityUpdate < this.activityUpdateCooldown) {
      // Clear existing timer and set a new one
      if (this.activityThrottleTimer) {
        clearTimeout(this.activityThrottleTimer);
      }
      this.activityThrottleTimer = setTimeout(() => {
        this.sendActivityUpdate();
        this.lastActivityUpdate = Date.now();
      }, this.activityUpdateCooldown - (now - this.lastActivityUpdate));
      return;
    }
    
    // Send activity update to server
    this.sendActivityUpdate();
    this.lastActivityUpdate = now;
  }

  setActive() {
    this.isActive = true;
    this.updateActivity();
  }

  setInactive() {
    this.isActive = false;
    this.updateActivity();
  }

  trackSessionStart() {
    this.sendLiveEvent('session_start', {
      sessionId: this.sessionId,
      userId: this.userId,
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      referrer: document.referrer,
      url: window.location.href
    });
  }

  trackSessionEnd() {
    this.sendLiveEvent('session_end', {
      sessionId: this.sessionId,
      userId: this.userId,
      duration: Date.now() - this.startTime,
      lastActivity: this.lastActivity
    });
  }

  trackStepChange(step, stepData = {}) {
    this.currentStep = step;
    this.updateActivity();
    
    this.sendLiveEvent('step_change', {
      sessionId: this.sessionId,
      userId: this.userId,
      step: step,
      stepName: this.getStepName(step),
      ...stepData
    });
  }

  trackCartAddition(item, price, sku) {
    this.sendLiveEvent('cart_addition', {
      sessionId: this.sessionId,
      userId: this.userId,
      item: item,
      price: price,
      sku: sku,
      timestamp: new Date().toISOString()
    });
    
    // Debug logging to see what we're sending
    // console.log('🛒 Live monitoring trackCartAddition:', {
    //   item: item,
    //   price: price,
    //   sku: sku,
    //   itemPrice: item.price,
    //   sessionId: this.sessionId,
    //   userId: this.userId,
    //   priceType: typeof price,
    //   itemPriceType: typeof item.price
    // });
  }

  trackCableCompletion(config, totalPrice) {
    this.sendLiveEvent('cable_completion', {
      sessionId: this.sessionId,
      userId: this.userId,
      config: config,
      totalPrice: totalPrice,
      timestamp: new Date().toISOString()
    });
  }

  trackError(errorType, errorData) {
    this.sendLiveEvent('error', {
      sessionId: this.sessionId,
      userId: this.userId,
      errorType: errorType,
      errorData: errorData,
      timestamp: new Date().toISOString()
    });
  }

  trackPageView(page) {
    this.sendLiveEvent('page_view', {
      sessionId: this.sessionId,
      userId: this.userId,
      page: page,
      timestamp: new Date().toISOString()
    });
  }

  getStepName(step) {
    const stepNames = {
      0: 'Welcome',
      1: 'Cable Type Selection',
      2: 'Style Selection',
      3: 'Connector A Selection',
      4: 'Connector B Selection',
      5: 'Cable Length',
      6: 'Review & Pricing',
      7: 'Complete'
    };
    return stepNames[step] || `Step ${step}`;
  }

  getEventPriority(eventType) {
    const priorities = {
      'session_start': 'high',
      'session_end': 'high',
      'error': 'high',
      'step_change': 'normal',
      'cart_addition': 'normal',
      'cable_completion': 'normal',
      'page_view': 'low'
    };
    return priorities[eventType] || 'normal';
  }

  async sendLiveEvent(eventType, data) {
    const eventData = {
      type: eventType,
      sessionId: this.sessionId,
      userId: this.userId,
      timestamp: new Date().toISOString(),
      data: data
    };

    // Debug logging to see what we're sending
    // console.log('📤 Live monitoring sendLiveEvent:', {
    //   eventType: eventType,
    //   eventData: eventData,
    //   data: data
    // });

    // Queue the request instead of sending immediately
    const priority = this.getEventPriority(eventType);
    
    const requestId = requestQueue.addRequest({
      type: 'live_event',
      endpoint: `${this.apiBaseUrl}/api/live-monitoring`,
      data: eventData,
      priority: priority
    });
    
    // console.log('📥 Live event queued with ID:', requestId);
  }

  async sendActivityUpdate() {
    const activityData = {
      type: 'activity_update',
      sessionId: this.sessionId,
      userId: this.userId,
      currentStep: this.currentStep,
      isActive: this.isActive,
      lastActivity: this.lastActivity,
      sessionDuration: Date.now() - this.startTime,
      timestamp: new Date().toISOString()
    };

    // Queue the request instead of sending immediately
    requestQueue.addRequest({
      type: 'activity_update',
      endpoint: `${this.apiBaseUrl}/api/live-monitoring`,
      data: activityData,
      priority: 'low' // Activity updates are low priority
    });
  }

  storeOfflineEvent(eventType, data) {
    try {
      const offlineEvents = JSON.parse(localStorage.getItem('offline_live_events') || '[]');
      offlineEvents.push({
        type: eventType,
        data: data,
        timestamp: new Date().toISOString()
      });
      
      // Keep only last 100 events
      if (offlineEvents.length > 100) {
        offlineEvents.splice(0, offlineEvents.length - 100);
      }
      
      localStorage.setItem('offline_live_events', JSON.stringify(offlineEvents));
    } catch (error) {
      console.warn('Failed to store offline event:', error);
    }
  }

  async syncOfflineEvents() {
    try {
      const offlineEvents = JSON.parse(localStorage.getItem('offline_live_events') || '[]');
      
      if (offlineEvents.length === 0) return;

      for (const event of offlineEvents) {
        await this.sendLiveEvent(event.type, event.data);
      }

      // Clear offline events after successful sync
      localStorage.removeItem('offline_live_events');
      
    } catch (error) {
      console.warn('Failed to sync offline events:', error);
    }
  }

  destroy() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    if (this.activityThrottleTimer) {
      clearTimeout(this.activityThrottleTimer);
    }
    this.trackSessionEnd();
    
    // Stop queue processing after a delay to allow final requests to complete
    setTimeout(() => {
      requestQueue.stopProcessing();
    }, 5000);
  }

  // Public API methods
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      currentStep: this.currentStep,
      isActive: this.isActive,
      lastActivity: this.lastActivity,
      sessionDuration: Date.now() - this.startTime,
      startTime: this.startTime
    };
  }
}

// Create singleton instance
const liveMonitoringService = new LiveMonitoringService();

// Export for use in components
export default liveMonitoringService;

// Also attach to window for global access
if (typeof window !== 'undefined') {
  window.liveMonitoringService = liveMonitoringService;
} 